import { onError } from 'apollo-link-error';

import { setGraphqlErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { NoNetworkError, getTraceIdFromResponse } from '@confluence/network';
import { processError } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import type { ApolloLink } from 'apollo-link';

export const createOnErrorLink = (): ApolloLink =>
	onError(({ operation, graphQLErrors, networkError }) => {
		let response: Response | undefined = operation.getContext()?.response;
		if (!response) {
			// 'response' may not always be populated in context, so try
			// to get the Response object from graphQLErrors:
			response = graphQLErrors
				?.map((e) => getResponseFromError(e.originalError))
				.find((e) => e != null);
		}

		const traceId = getTraceIdFromResponse(response);

		if (graphQLErrors?.length) {
			graphQLErrors.forEach((e) => {
				setGraphqlErrorAttributes(e, {
					graphqlPath: e.path?.join('.'),
					operationName: operation.operationName,
					statusCode: response?.status,
					traceId,
				});
				processError(e);
			});
		}

		if (networkError) {
			setGraphqlErrorAttributes(networkError, {
				operationName: operation.operationName,
				statusCode: getResponseFromError(networkError)?.status,
				traceId,
				...(networkError instanceof NoNetworkError && networkError.wasBubbledUp === true
					? { wasBubbledUp: true } // Extra analytics attribute to mark errors caused by platform_editor_bubble_cfe_gql_network_errors. This can probably be removed during fg cleanup.
					: {}),
			});
			processError(networkError);
		}
	});

function getResponseFromError(error: Error | null | undefined) {
	if (typeof error === 'object' && error && 'response' in error) {
		const { response } = error;
		if (typeof response === 'object' && response && 'status' in response) {
			return response as Response;
		}
	}
	return undefined;
}
